import type { PlanChangePreview } from '@shared/payments'
import type { LocaleId } from '@shared/types'
import { money } from './PricingConfigurator'
import { Banner } from '../ui'

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : null

/**
 * Explains what saving a configuration change will do to the customer's bill. The two
 * processors behave differently, so this states the actual outcome rather than a generic
 * "changes take effect on your next invoice".
 */
export function PlanChangeNotice({
  preview,
  localeId,
  pending,
}: {
  preview: PlanChangePreview
  localeId: LocaleId
  /** True while the preview is still being priced, so stale figures are not shown as final. */
  pending?: boolean
}) {
  if (pending) {
    return <Banner tone="info">Checking what this change costs…</Banner>
  }

  if (preview.timing === 'not_billed_yet') {
    return (
      <Banner tone="info">
        Nothing is charged for this change. Your first invoice uses whatever configuration is
        saved when your trial ends.
      </Banner>
    )
  }

  if (preview.timing === 'next_cycle') {
    const on = formatDate(preview.effectiveAt)
    return (
      <Banner tone="info">
        This change takes effect{on ? ` on ${on}` : ' at your next billing date'}. Razorpay does
        not split a billing period, so you keep what you have already paid for until then and the
        new price starts with the next cycle.
      </Banner>
    )
  }

  const currency = preview.currency ?? 'USD'
  const due = preview.amountDueMinor ?? 0
  const credit = preview.creditMinor ?? 0
  const fmt = (minor: number) => money(Math.abs(minor), currency, 100, localeId)

  // A downgrade can leave more credit than the new charge, which Stripe carries forward.
  if (due <= 0) {
    return (
      <Banner tone="success">
        This change applies immediately. You have {fmt(credit)} of unused time credited, which
        covers the new price — {fmt(due)} is due now and any remainder rolls into your next
        invoice.
      </Banner>
    )
  }

  return (
    <Banner tone="info">
      This change applies immediately.{' '}
      {credit > 0
        ? `You are credited ${fmt(credit)} for unused time on your current plan, so ${fmt(due)} is due on your next invoice.`
        : `${fmt(due)} is due on your next invoice, prorated from today.`}
    </Banner>
  )
}
